import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { CoachCardMinimalData } from "~/components/coach-card";
import { CountrySelect } from "~/components/country-select";
import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ControlledRadioGroup } from "~/components/form/controlled-inputs";
import { LanguageSelect } from "~/components/language-select";
import { ConditionalField } from "~/components/ui/conditional-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import {
  getDiscoveryState,
  updateDiscoveryState,
  useDiscoveryState,
} from "~/lib/discovery/state";
import { processRefinement } from "~/lib/functions/refinement";
import {
  sessionDeliveryMethodsFeild,
  prefersCoachLocationField,
  faithIntegrationsField,
  type CoachDiscoveryForm,
} from "~/lib/schemas/coach-discovery";

// ---------------------------------------------------------------------------
// Schema for section 3
// ---------------------------------------------------------------------------

const Section3Schema = z.object({
  sessionDeliveryMethods: z
    .string()
    .min(1, "Please select a session delivery method."),
  prefersCoachLocation: z.string().min(1, "Please select a preference."),
  coachLocationCountry: z.string().optional(),
  coachLocationRegion: z.string().optional(),
  faithIntegration: z.array(z.string()).min(1, "Please select at least one."),
  otherFaithIntegration: z.string().optional(),
  preferredLanguages: z
    .array(z.string())
    .min(1, "Please select at least one language.")
    .max(3, "You can select up to 3 languages."),
});

type Section3Form = z.infer<typeof Section3Schema>;

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/refine/logistics")({
  component: RefineLogistics,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function RefineLogistics() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { form: discoveryForm } = useDiscoveryState();
  const [isProcessing, setIsProcessing] = useState(false);
  const otherFaithRef = useRef<HTMLTextAreaElement>(null);

  const defaultValues = useMemo(
    (): Section3Form => ({
      sessionDeliveryMethods: discoveryForm.sessionDeliveryMethods,
      prefersCoachLocation: discoveryForm.prefersCoachLocation,
      coachLocationCountry: discoveryForm.coachLocationCountry ?? "",
      coachLocationRegion: discoveryForm.coachLocationRegion ?? "",
      faithIntegration: discoveryForm.faithIntegration,
      otherFaithIntegration: discoveryForm.otherFaithIntegration ?? "",
      preferredLanguages: discoveryForm.preferredLanguages,
    }),
    [discoveryForm],
  );

  const { handleSubmit, control, watch, formState, setValue, register } =
    useForm<Section3Form>({
      resolver: zodResolver(Section3Schema),
      defaultValues,
      mode: "onTouched",
    });

  const { errors, isSubmitting } = formState;

  const watchedDelivery = watch("sessionDeliveryMethods");
  const showLocation =
    watchedDelivery === "In-person" || watchedDelivery === "Hybrid";
  const prefersLocation = watch("prefersCoachLocation");
  const showLocationDetails = prefersLocation === "Yes";
  const watchedFaith = watch("faithIntegration") || [];
  const showOtherFaith = watchedFaith.includes("Other");
  const watchedLanguages = watch("preferredLanguages") || [];

  // Auto-set location preference when delivery method changes
  const prevDeliveryRef = useRef(watchedDelivery);
  useEffect(() => {
    if (watchedDelivery === prevDeliveryRef.current) return;
    prevDeliveryRef.current = watchedDelivery;

    if (watchedDelivery === "In-person" || watchedDelivery === "Hybrid") {
      setValue("prefersCoachLocation", "Yes");
    } else {
      setValue("prefersCoachLocation", "No preference");
      setValue("coachLocationCountry", "");
      setValue("coachLocationRegion", "");
    }
  }, [watchedDelivery, setValue]);

  const onSubmit = async (data: Section3Form) => {
    // Merge this section into localStorage
    updateDiscoveryState(data);

    // Get the full form and sanitize cross-field dependencies
    const raw = getDiscoveryState();
    const requiresLocation = raw.prefersCoachLocation === "Yes";
    const fullForm: CoachDiscoveryForm = {
      ...raw,
      otherGoal: raw.otherGoal?.trim() || undefined,
      otherFaithIntegration: raw.otherFaithIntegration?.trim() || undefined,
      coachLocationCountry: requiresLocation
        ? raw.coachLocationCountry
        : undefined,
      coachLocationRegion: requiresLocation
        ? raw.coachLocationRegion
        : undefined,
    };

    setIsProcessing(true);

    try {
      const result = await processRefinement({ data: fullForm });

      if (result.status === "error") {
        toast.error(result.message);
        setIsProcessing(false);
        return;
      }

      if (result.status === "not_found" || result.matches.length === 0) {
        toast.error(
          "No coaches matched your exact criteria. Try adjusting your preferences.",
        );
        setIsProcessing(false);
        return;
      }

      // Put results in query cache for the results page
      queryClient.setQueryData<CoachCardMinimalData[]>(
        ["quickMatchResults"],
        result.matches,
      );

      void navigate({ to: "/discovery/results" });
    } catch {
      toast.error("Something went wrong. Please try again.");
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    void navigate({ to: "/discovery/refine/fit" });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title="Logistics & Preferences" />

      {/* Session delivery */}
      <DiscoveryStep.Card>
        <FormField
          label={
            sessionDeliveryMethodsFeild.subTitle || "Session delivery method"
          }
          labelSize="xl"
          error={errors.sessionDeliveryMethods?.message}
        >
          <ControlledRadioGroup
            control={control}
            name="sessionDeliveryMethods"
            options={
              sessionDeliveryMethodsFeild.options?.map(({ value, label }) => ({
                value,
                label,
              })) || []
            }
            className="flex flex-col gap-y-4"
          />
        </FormField>
      </DiscoveryStep.Card>

      {/* Location preference (conditional on in-person/hybrid) */}
      {showLocation && (
        <DiscoveryStep.Card>
          <FormField
            label={
              prefersCoachLocationField.subTitle ||
              "Do you prefer a coach in your area?"
            }
            labelSize="xl"
            error={errors.prefersCoachLocation?.message}
          >
            <ControlledRadioGroup
              control={control}
              name="prefersCoachLocation"
              options={
                prefersCoachLocationField.options?.map(({ value, label }) => ({
                  value,
                  label,
                })) || []
              }
              className="flex flex-col gap-y-4"
            />
          </FormField>

          {showLocationDetails && (
            <div className="mt-6 space-y-4">
              <FormField
                label="Country"
                error={errors.coachLocationCountry?.message}
              >
                <Controller
                  control={control}
                  name="coachLocationCountry"
                  render={({ field }) => (
                    <CountrySelect
                      value={field.value || ""}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormField>

              <FormField
                label="Region / State (optional)"
                error={errors.coachLocationRegion?.message}
              >
                <Controller
                  control={control}
                  name="coachLocationRegion"
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="e.g. California, London, etc."
                    />
                  )}
                />
              </FormField>
            </div>
          )}
        </DiscoveryStep.Card>
      )}

      {/* Faith integration */}
      <DiscoveryStep.Card>
        <FormField
          label={"Faith or spirituality preference"}
          labelSize="xl"
          error={errors.faithIntegration?.message}
        >
          <CheckboxGroupField
            name="faithIntegration"
            control={control}
            options={faithIntegrationsField.options || []}
            exclusiveOptions={["No preference"]}
            onTrigger={{
              Other: () => {
                setTimeout(() => otherFaithRef.current?.focus(), 100);
              },
            }}
          />
        </FormField>

        <ConditionalField show={showOtherFaith}>
          <FormField
            label="Please describe"
            error={errors.otherFaithIntegration?.message}
          >
            <Controller
              control={control}
              name="otherFaithIntegration"
              render={({ field }) => (
                <Textarea
                  {...field}
                  ref={otherFaithRef}
                  placeholder="Describe your faith or spiritual preference..."
                  maxLength={500}
                />
              )}
            />
          </FormField>
        </ConditionalField>
      </DiscoveryStep.Card>

      {/* Language preferences */}
      <DiscoveryStep.Card>
        <FormField
          label="Preferred coaching languages"
          labelSize="xl"
          error={errors.preferredLanguages?.message}
        >
          <LanguageSelect
            field={{
              name: "preferredLanguages",
              value: watchedLanguages,
            }}
            props={register("preferredLanguages")}
          />
          {watchedLanguages.length > 3 && (
            <p className="mt-2 text-sm text-red-600">
              You can select up to 3 languages only.
            </p>
          )}
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control
        prev={handleBack}
        type="submit"
        disabled={isProcessing}
        loading={isSubmitting || isProcessing}
      />
    </form>
  );
}
